import styled from 'styled-components'
import { dayjsLondon } from 'lib/dayjs'

import { H, P, Wrapper } from '@farewill/ui'
import { COLOR, GTR } from '@farewill/ui/tokens'
import { Checkbox } from 'components/form'
import {
  ProbateEstatePerson,
  PROBATE_SIGNATURE_REQUEST_DOCUMENTS,
} from 'lib/models/probate-estate'
import { usePeople } from '../helpers/people-context'
import {
  getSignatureRequestSentDate,
  getSignatureRequestSignedDate,
} from '../helpers/complete-sections/section-referral'

const StyledWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: ${GTR.M};
`

export function RenouncingExecutorsChecks({
  people,
}: {
  people: ProbateEstatePerson[]
}) {
  if (people.length === 0) {
    return null
  }

  return (
    <Wrapper margin={['L', 0, 0]}>
      <H size="XS">Renouncing executors</H>
      <P color="GREY.DARK">
        A renouncing executor must sign a deed of renunciation.
      </P>

      {people.map((person, index) => {
        const sentDate = getSignatureRequestSentDate(
          person,
          PROBATE_SIGNATURE_REQUEST_DOCUMENTS.DEED_OF_RENUNCIATION
        )
        const signedDate = getSignatureRequestSignedDate(
          person,
          PROBATE_SIGNATURE_REQUEST_DOCUMENTS.DEED_OF_RENUNCIATION
        )

        const sentOrSignedTime = {
          prefix: signedDate ? 'Marked as received' : 'Marked as requested',
          date: signedDate || sentDate || null,
        }

        const personName = `${person.attributes.title ?? ''} ${
          person.attributes.firstNames
        } ${person.attributes.lastName}`.trim()

        return (
          <Wrapper
            bordered
            padding="M"
            margin={['L', 0, 0]}
            borderRadius="M"
            key={`renouncing-executor-${index}`}
          >
            <H size="XS" margin="0">
              {personName}
            </H>

            <StyledWrapper margin={['M', 0, 0]}>
              <RenouncingExecutorCheckbox
                index={index}
                person={person}
                field="sent"
                disabled={!!signedDate}
              />

              <RenouncingExecutorCheckbox
                index={index}
                person={person}
                field="signed"
                disabled={!sentDate}
              />

              {sentOrSignedTime.date ? (
                <P color={COLOR.GREY.MEDIUM}>
                  {sentOrSignedTime.prefix} on{' '}
                  {dayjsLondon(sentOrSignedTime.date).format('Do MMMM YYYY')}
                </P>
              ) : null}
            </StyledWrapper>
          </Wrapper>
        )
      })}
    </Wrapper>
  )
}

function RenouncingExecutorCheckbox({
  index,
  person,
  field,
  disabled,
}: {
  index: number
  person: ProbateEstatePerson
  field: 'sent' | 'signed'
  disabled?: boolean
}) {
  const { updatePersonSignatureRequest } = usePeople()

  const sentDate = getSignatureRequestSentDate(
    person,
    PROBATE_SIGNATURE_REQUEST_DOCUMENTS.DEED_OF_RENUNCIATION
  )
  const signedDate = getSignatureRequestSignedDate(
    person,
    PROBATE_SIGNATURE_REQUEST_DOCUMENTS.DEED_OF_RENUNCIATION
  )

  const checked = field === 'sent' ? !!sentDate : !!signedDate

  return (
    <Checkbox
      name={`renouncingExecutors[${index}].${field}`}
      disabled={disabled}
      checked={checked}
      handleSave={({ value: checked }) => {
        if (field === 'sent') {
          updatePersonSignatureRequest({
            personId: person.id,
            sent: checked === 'true',
            signed: false,
            documentName:
              PROBATE_SIGNATURE_REQUEST_DOCUMENTS.DEED_OF_RENUNCIATION,
          })
        } else {
          updatePersonSignatureRequest({
            personId: person.id,
            sent: true,
            signed: checked === 'true',
            documentName:
              PROBATE_SIGNATURE_REQUEST_DOCUMENTS.DEED_OF_RENUNCIATION,
          })
        }
      }}
      label={field === 'sent' ? 'Deed requested' : 'Deed received'}
    />
  )
}
